import React, { useState, useEffect } from 'react';
import { Commute } from '../types';
import { api } from '../utils/api';
import { processCommute, formatDuration, formatDateTime, filterCommutesByTime } from '../utils/commuteUtils';

interface CommuteListProps {
  userId: number;
  selectedCommute: number | null;
  onCommuteSelect: (commuteId: number) => void;
  timeFilter: 'today' | 'week' | 'all';
}

const CommuteList: React.FC<CommuteListProps> = ({ userId, selectedCommute, onCommuteSelect, timeFilter }) => {
  const [commutes, setCommutes] = useState<Commute[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCommutes = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedCommutes = await api.getCommutes(userId);
        if (Array.isArray(fetchedCommutes)) {
          setCommutes(fetchedCommutes);
        } else {
          setCommutes([]);
          setError('Invalid response format from API');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch commutes');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchCommutes();
    }
  }, [userId]);

  // Filter commutes based on time filter
  const filteredCommutes = filterCommutesByTime(commutes, timeFilter);

  if (loading) {
    return (
      <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow p-6">
        <div className="h-96 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <div className="text-lg mt-4 text-gray-600 dark:text-gray-300">Loading commutes...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow p-6">
        <div className="h-96 flex items-center justify-center">
          <div className="text-red-600 text-lg">Error: {error}</div>
        </div>
      </div>
    );
  }

  if (!Array.isArray(filteredCommutes) || filteredCommutes.length === 0) {
    return (
      <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow p-6">
        <div className="h-96 flex items-center justify-center">
          <div className="text-gray-500 text-lg">
            No commutes found
            {timeFilter !== 'all' && (
              <span className="block text-sm mt-1">
                for {timeFilter === 'today' ? 'today' : 'the last week'}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-xl font-semibold text-gray-900">Commute List</h2>
        <p className="text-gray-600 text-sm mt-1">
          {Array.isArray(filteredCommutes) ? filteredCommutes.length : 0} commute{Array.isArray(filteredCommutes) && filteredCommutes.length !== 1 ? 's' : ''} found
          {timeFilter !== 'all' && (
            <span className="ml-2 text-blue-600">
              ({timeFilter === 'today' ? 'today' : 'last week'})
            </span>
          )}
        </p>
      </div>
      
      <div className="max-h-96 overflow-y-auto">
        {Array.isArray(filteredCommutes) && filteredCommutes.map((commute) => {
          const processedCommute = processCommute(commute);
          const isSelected = selectedCommute === commute.id;
          
          return (
            <div
              key={commute.id}
              className={`p-4 border-b cursor-pointer transition-colors ${
                isSelected ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'
              }`}
              onClick={() => onCommuteSelect(commute.id)}
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-medium text-gray-900">
                  Commute #{commute.id}
                </h3>
                <span className="text-sm text-gray-500">
                  {formatDuration(processedCommute.duration)}
                </span>
              </div>
              
              <div className="space-y-1 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Start:</span> {formatDateTime(processedCommute.startDate)}
                </div>
                <div>
                  <span className="font-medium">End:</span> {formatDateTime(processedCommute.endDate)}
                </div>
                <div>
                  <span className="font-medium">Points:</span> {commute.gps_points.length} GPS coordinates
                </div>
              </div>
              
              {isSelected && (
                <div className="mt-3 p-3 bg-blue-100 rounded-md">
                  <p className="text-sm text-blue-800">
                    This commute is highlighted on the map
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CommuteList;
